import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { UserPlus, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import { LOGO_FULL } from "../lib/vehicleImages";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!accept) { setError("You must accept the Terms of Use to create an account."); return; }
    setBusy(true); setError(null);
    try {
      await register({ ...form, accept_terms: true, accept_privacy: true });
      navigate("/verify-email");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5 py-16" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_FULL} alt="ATLAS" className="h-12 w-auto mx-auto mb-3" />
          <h1 className="font-display text-3xl font-bold tracking-tight">Create your account</h1>
          <p className="text-muted-foreground mt-1">Find better. Compare smarter.</p>
        </div>
        <form onSubmit={submit} className="bg-card border border-border rounded-md p-6 md:p-8 space-y-5">
          <GoogleSignInButton label="Sign up with Google" />
          <p className="text-[11px] text-muted-foreground text-center -mt-2">
            By continuing with Google you agree to the{" "}
            <Link to="/legal/terms" target="_blank" className="text-atlas-blue">Terms of Use</Link> and{" "}
            <Link to="/legal/privacy" target="_blank" className="text-atlas-blue">Privacy Notice</Link>.
          </p>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" /> or sign up with email <span className="h-px flex-1 bg-border" />
          </div>
          <div className="space-y-1.5">
            <Label className="overline">Name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)}
              placeholder="Your name" required data-testid="register-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="overline">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
              placeholder="you@example.com" required data-testid="register-email" />
          </div>
          <div className="space-y-1.5">
            <Label className="overline">Password</Label>
            <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)}
              placeholder="At least 8 characters" minLength={8} required data-testid="register-password" />
          </div>

          <div className="flex items-start gap-3 rounded-md border border-border bg-muted/30 p-3">
            <Checkbox id="accept-terms" checked={accept} onCheckedChange={(v) => setAccept(!!v)}
              data-testid="register-accept-terms" className="mt-0.5" />
            <label htmlFor="accept-terms" className="text-sm text-slate-700 leading-relaxed cursor-pointer">
              I have read and accept the{" "}
              <Link to="/legal/terms" target="_blank" className="text-atlas-blue font-medium">Terms of Use</Link>{" "}
              and the{" "}
              <Link to="/legal/privacy" target="_blank" className="text-atlas-blue font-medium">Privacy Notice</Link>.
            </label>
          </div>

          {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}

          <Button type="submit" disabled={busy || !accept} data-testid="register-submit"
            className="w-full bg-primary hover:bg-atlas-blue text-white py-6">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating account…</> : <><UserPlus className="h-4 w-4 mr-2" /> Create account</>}
          </Button>
          <p className="text-sm text-muted-foreground text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-atlas-blue font-medium" data-testid="register-goto-login">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
